import { Currency, InputMasked, Select } from '@chiroup/components';
import { AppointmentInsuranceType } from '@chiroup/core';
import { useEffect, useState } from 'react';
import { getBillingPriorityError } from './EncounterInsurancePolicy';

type Props = {
  index: number;
  insurances: Partial<AppointmentInsuranceType>[];
  setInsurances: (ins: Partial<AppointmentInsuranceType>[]) => void;
  readOnly?: boolean;
  insuranceErrors:
    | {
        index: number;
        field: keyof AppointmentInsuranceType;
        message: string;
      }[]
    | [];
  coPay?: number;
  coInsurance?: number;
  allowBillingPriorityChange: boolean;
};

const EncounterInsuranceFormFields: React.FC<Props> = ({
  index,
  setInsurances,
  insurances,
  readOnly = false,
  insuranceErrors,
  coPay = 0,
  coInsurance = 0,
  allowBillingPriorityChange,
}) => {
  const [selectedBillingPriorities, setSelectedBillingPriorities] = useState<
    number[]
  >([]);

  useEffect(() => {
    if (insurances.length) {
      const validBillingPriorities = insurances
        .map((policy) => policy.billingPriority)
        .filter((priority): priority is number => priority !== undefined);

      setSelectedBillingPriorities(validBillingPriorities);
    }
  }, [insurances, setSelectedBillingPriorities]);

  const options = Array.from({ length: insurances?.length ?? 0 }, (_, i) => ({
    text: (i + 1).toString(),
    value: i + 1,
  })).filter(
    (option) =>
      !selectedBillingPriorities.includes(option.value) ||
      option.value === insurances?.[index]?.billingPriority,
  );

  function updatePolicy<K extends keyof AppointmentInsuranceType>(
    fieldName: K,
    fieldValue: any,
  ) {
    const objs = insurances?.map((policy, i) => {
      if (index === i) {
        return {
          ...policy,
          [fieldName]: fieldValue,
        };
      }
      return policy;
    });

    setInsurances(objs);
  }

  return (
    <>
      <Select
        name="billingPriority"
        label="Billing Priority *"
        value={insurances?.[index]?.billingPriority}
        onChange={(e) => updatePolicy('billingPriority', e)}
        options={options}
        disabled={readOnly || !allowBillingPriorityChange}
        limit={1}
        className="col-span-2"
        clearable
        errors={getBillingPriorityError(index, insuranceErrors)}
      />
      <InputMasked
        label="Deductible"
        name="deductible"
        placeholder="0"
        className="col-span-2"
        value={insurances?.[index]?.deductible}
        onChange={(val) => updatePolicy('deductible', val)}
        disabled={readOnly}
      />

      {!!coPay && (
        <div className="block text-sm font-medium leading-5 text-gray-900 sm:mt-px sm:pt-2 dark:text-darkGray-200 col-span-2">
          {`Co-pay: `}
          <Currency value={coPay} />
        </div>
      )}
      {!!coInsurance && (
        <div className="block text-sm font-medium leading-5 text-gray-900 sm:mt-px sm:pt-2 dark:text-darkGray-200 col-span-2">{`Co-insurance: ${coInsurance}% `}</div>
      )}
    </>
  );
};

export default EncounterInsuranceFormFields;
